import { PayInvoicePageContext } from "@auer-steel/Pages/PayInvoicePage";
import { InvoicePaymentModel } from "@auer-steel/Services/InvoicePaymentService";
import { AuerState } from "@auer-steel/Store/AuerState";
import updateInvoicePayments from "@auer-steel/Store/Pages/PayInvoice/Handlers/UpdateInvoicePayments";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Link from "@insite/mobius/Link";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { produce } from "immer";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: AuerState) => ({
    invoiceCollection: state.payInvoice.invoicePaymentCollection,
    language: state.context.session.language,
    currencySymbol: state.context.session.currency?.currencySymbol ?? "",
});

const mapDispatchToProps = {
    updateInvoicePayments,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoicePaymentsTableStyles {
    container?: InjectableCss;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    invoiceNumberHeader?: DataTableHeaderProps;
    invoiceDateHeader?: DataTableHeaderProps;
    dueDateHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    poNumberHeader?: DataTableHeaderProps;
    amountPaidHeader?: DataTableHeaderProps;
    totalHeader?: DataTableHeaderProps;
    currentBalanceHeader?: DataTableHeaderProps;
    inputHeader?: DataTableHeaderProps;
    invoiceNumberCell?: DataTableCellBaseProps;
    invoiceDateCell?: DataTableCellBaseProps;
    dueDateCell?: DataTableCellBaseProps;
    shipToCell?: DataTableCellBaseProps;
    poNumberCell?: DataTableCellBaseProps;
    totalCell?: DataTableCellBaseProps;
    currentBalanceCell?: DataTableCellBaseProps;
    amountPaidCell?: DataTableCellBaseProps;
    inputCell?: DataTableCellBaseProps;
    amountCell?: TextFieldProps;
}

export const tableStyles: InvoicePaymentsTableStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    amountPaidHeader: {
        alignX: "right",
    },
    totalHeader: {
        alignX: "right",
    },
    currentBalanceHeader: {
        alignX: "right",
    },
    inputHeader: {
        alignX: "center",
    },
    totalCell: {
        alignX: "right",
    },
    amountPaidCell: {
        alignX: "right",
    },
    currentBalanceCell: {
        alignX: "right",
    },
    inputCell: {
        alignX: "center",
    },
    amountCell: {
        css: css`
            text-align: right;
        `,
        errorProps: {
            css: css`
                white-space: break-spaces;
            `,
        },
    },
};

interface PaymentAmountValue {
    [key: string]: string;
}

const styles = tableStyles;

const InvoicePaymentsTable = (props: Props) => {
    const { invoicePayments } = props.invoiceCollection;
    if (invoicePayments.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No invoices selected")}</Typography>
            </StyledWrapper>
        );
    }
    const initalPaymentValues: PaymentAmountValue = {};
    invoicePayments.forEach((item) => {
        initalPaymentValues[item.invoice.invoiceNumber] = `${item.invoice.currentBalance}`
    });

    const [paymentValue, setPaymentValue] = React.useState<PaymentAmountValue>(initalPaymentValues);

    const rows = invoicePayments.map(invoicePayment => {
        const invoice = invoicePayment.invoice;
        return {
            id: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            date: invoice.invoiceDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.invoiceDate),
                      language: props.language,
                  })
                : "",
            terms: invoice.terms,
            dueDate: invoice.dueDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.dueDate),
                      language: props.language,
                  })
                : "",
            shipTo: `${invoice.stCompanyName} ${invoice.btAddress1} ${invoice.btAddress2} ${invoice.shipToCity} ${invoice.shipToState}`,
            customerPO: invoice.customerPO,
            status: invoice.status,
            total: invoice.invoiceTotalDisplay,
            currentBalance: invoice.currentBalanceDisplay,
            payInFull: invoicePayment.payInFull,
            enteredPaymentAmount: invoicePayment.enteredPaymentAmount,
            enteredPaymentAmountError: invoicePayment.enteredPaymentAmountError,
            amountPaid: props.language
                ? getLocalizedCurrency({
                      amount: invoice.invoiceTotal - invoice.currentBalance,
                      language: props.language,
                      currencySymbol: props.currencySymbol,
                  })
                : invoice.invoiceTotal - invoice.currentBalance,
            termsAmount: invoice.properties["termsAmount"],
            termsDueDate: invoice.properties["termsDueDate"]
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.properties["termsDueDate"]),
                      language: props.language,
                  })
                : "",
        };
    });

    const removeInvoice = (invoiceNumber: string) => {
        const newCollection = {
            ...props.invoiceCollection,
            invoicePayments: invoicePayments.filter(invoicePayment => {
                return invoicePayment.invoice.invoiceNumber !== invoiceNumber;
            }),
        };
        props.updateInvoicePayments(newCollection);
    };

    const setPayInFull = (invoiceNumber: string) => {
        const selectedInvoicePayment = props.invoiceCollection.invoicePayments.find(invoicePayment => {
            return invoicePayment.invoice.invoiceNumber === invoiceNumber;
        });
        if (selectedInvoicePayment) {
            const paymentAmount = selectedInvoicePayment.invoice.currentBalance
            
            setPaymentValue({ ...paymentValue, [invoiceNumber]: `${paymentAmount}` });
            const newInvoicePayment = {
                ...selectedInvoicePayment,
                enteredPaymentAmountError: undefined,
                payInFull: !selectedInvoicePayment.payInFull,
                paymentAmount,
                enteredPaymentAmount: Number(paymentAmount).toFixed(2),
                
            };
            const replacementIndex = props.invoiceCollection.invoicePayments.indexOf(selectedInvoicePayment);
            props.updateInvoicePayments({
                ...props.invoiceCollection,
                invoicePayments: produce(props.invoiceCollection.invoicePayments, draft => {
                    draft[replacementIndex] = newInvoicePayment;
                }),
            });
        }
    };

    const handleInvoicePaymentAmountChange = (value: string, invoiceNumber: string) => {
        setPaymentValue({ ...paymentValue, [invoiceNumber]: value });
        const selectedInvoicePayment = props.invoiceCollection.invoicePayments.find(invoicePayment => {
            return invoicePayment.invoice.invoiceNumber === invoiceNumber;
        });
        if (selectedInvoicePayment) {
            const newInvoicePayment = {
                ...selectedInvoicePayment,
                enteredPaymentAmount: value,
            };
            if (validateInvoicePayment(newInvoicePayment)) {
                newInvoicePayment.paymentAmount = Number(newInvoicePayment.enteredPaymentAmount);
            }
            const replacementIndex = props.invoiceCollection.invoicePayments.indexOf(selectedInvoicePayment);
            props.updateInvoicePayments({
                ...props.invoiceCollection,
                invoicePayments: produce(props.invoiceCollection.invoicePayments, draft => {
                    draft[replacementIndex] = newInvoicePayment;
                }),
            });
        }
    };

    const handleInvoicePaymentAmountUpdate = (value: string, invoiceNumber: string) => {
        setPaymentValue({ ...paymentValue, [invoiceNumber]: value });
    };

    const validateInvoicePayment = (invoicePayment: InvoicePaymentModel) => {
        if (!invoicePayment.enteredPaymentAmount) {
            invoicePayment.enteredPaymentAmountError = translate("Amount is required");
            return false;
        }
        const parsedValue = Number(invoicePayment.enteredPaymentAmount);
        if (Number.isNaN(parsedValue)) {
            invoicePayment.enteredPaymentAmountError = translate("Amount must be numeric");
            return false;
        }
        if (parsedValue === 0) {
            invoicePayment.enteredPaymentAmountError = translate("Amount cannot be zero");
            return false;
        }
        if ((invoicePayment.invoice.currentBalance > 0 && parsedValue <= 0)) {
            invoicePayment.enteredPaymentAmountError = translate("Amount must be greater than zero");
            return false;
        }
        if ((invoicePayment.invoice.currentBalance < 0 && parsedValue >= 0)) {
            invoicePayment.enteredPaymentAmountError = translate("Amount must be less than zero");
            return false;
        }
        if (invoicePayment.invoice.currentBalance > 0 && invoicePayment.invoice.currentBalance < parsedValue) {
            invoicePayment.enteredPaymentAmountError = translate("Amount cannot be greater than the invoice total");
            return false;
        }
        if (invoicePayment.invoice.currentBalance < 0 && invoicePayment.invoice.currentBalance > parsedValue) {
            invoicePayment.enteredPaymentAmountError = translate("Amount cannot be less than the invoice total");
            return false;
        }
        invoicePayment.enteredPaymentAmountError = undefined;
        return true;
    };

    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader tight></DataTableHeader>
                    <DataTableHeader tight {...styles.invoiceNumberHeader}>
                        {translate("Invoice #")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.invoiceDateHeader}>
                        {translate("Invoice Date")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.dueDateHeader}>
                        {translate("Due Date")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.poNumberHeader}>
                        {translate("PO #")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.shipToHeader}>
                        {translate("Terms Amount")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.shipToHeader}>
                        {translate("Terms Due Date")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.totalHeader}>
                        {translate("Invoice Total")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.amountPaidHeader}>
                        {translate("Amount Paid")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.currentBalanceHeader}>
                        {translate("Current Balance")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.inputHeader}>
                        {translate("Pay In Full?")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.inputHeader}>
                        {translate("Payment Amount")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(
                        ({
                            id,
                            invoiceNumber,
                            date,
                            dueDate,
                            shipTo,
                            customerPO,
                            total,
                            currentBalance,
                            payInFull,
                            enteredPaymentAmount,
                            enteredPaymentAmountError,
                            amountPaid,
                            termsAmount,
                            termsDueDate,
                        }) => (
                            <DataTableRow key={id} {...styles.dataTableRow}>
                                <DataTableCell>
                                    <Clickable onClick={() => removeInvoice(invoiceNumber)}>
                                        <Icon src={X} />
                                    </Clickable>
                                </DataTableCell>
                                <DataTableCell {...styles.invoiceNumberCell}>
                                    <Link href={`/MyAccount/Invoices/Details?invoiceNumber=${invoiceNumber}`}>
                                        {invoiceNumber}
                                    </Link>
                                </DataTableCell>
                                <DataTableCell {...styles.invoiceDateCell}>{date}</DataTableCell>
                                <DataTableCell {...styles.dueDateCell}>{dueDate}</DataTableCell>
                                <DataTableCell {...styles.poNumberCell}>{customerPO}</DataTableCell>
                                <DataTableCell {...styles.shipToCell}>{termsAmount}</DataTableCell>
                                <DataTableCell {...styles.shipToCell}>{termsDueDate}</DataTableCell>
                                <DataTableCell {...styles.totalCell}>{total}</DataTableCell>
                                <DataTableCell {...styles.amountPaidCell}>{amountPaid}</DataTableCell>
                                <DataTableCell {...styles.currentBalanceCell}>{currentBalance}</DataTableCell>
                                <DataTableCell {...styles.inputCell}>
                                    <Checkbox
                                        onChange={() => {
                                            setPayInFull(invoiceNumber);
                                        }}
                                        checked={payInFull}
                                    />
                                </DataTableCell>
                                <DataTableCell {...styles.inputCell}>
                                    <TextField
                                        {...styles.amountCell}
                                        error={enteredPaymentAmountError}
                                        value={
                                            paymentValue[invoiceNumber] && !payInFull
                                                ? paymentValue[invoiceNumber]
                                                : Number(enteredPaymentAmount).toFixed(2)
                                        }
                                        disabled={payInFull}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            handleInvoicePaymentAmountChange(e.target.value, invoiceNumber)
                                        }
                                        onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            handleInvoicePaymentAmountUpdate(e.target.value, invoiceNumber)
                                        }
                                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target?.setSelectionRange(0, e.target?.value.length)}
                                    />
                                </DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoicePaymentsTable),
    definition: {
        group: "Pay Invoice" as any,
    },
};

export default widgetModule;
